// "Goes well with" products in the cart drawer
// (sections/cart-drawer-recommendations.liquid).
//
// The element is rendered by the cart drawer for small carts. It asks the
// recommendations API for products that complement the first cart item, and
// falls back to Shopify's automatic "related" products when no complementary
// ones are set up (Search & Discovery app). It stays hidden when neither
// returns anything addable. Until then it shows the skeleton placeholder the
// drawer renders inside it.
//
// The cart drawer replaces its markup on every cart change, which reconnects
// this element and reloads the list, so it never offers what is already in the
// cart.

const RECOMMENDATIONS_SECTION_ID = 'cart-drawer-recommendations';
const RECOMMENDATION_INTENTS = ['complementary', 'related'];
// Keeps the placeholder up at least this long, so a fast response does not
// flash it away.
const RECOMMENDATIONS_MIN_PLACEHOLDER_MS = 600;

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        this.load();
      }

      async load() {
        const productId = this.dataset.productId;
        if (!productId) return this.hide();

        const minDelay = new Promise((resolve) => setTimeout(resolve, RECOMMENDATIONS_MIN_PLACEHOLDER_MS));

        for (const intent of RECOMMENDATION_INTENTS) {
          try {
            const response = await fetch(
              `${this.dataset.url}?product_id=${productId}&limit=6&intent=${intent}&section_id=${RECOMMENDATIONS_SECTION_ID}`
            );
            if (!response.ok || !this.isConnected) continue;

            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const content = html.querySelector('.cart-recommendations__content');
            if (!content?.querySelector('.cart-recommendations__item')) continue;

            await minDelay;
            if (!this.isConnected) return;
            this.replaceChildren(...content.childNodes);
            this.removeAttribute('aria-busy');
            this.classList.add('cart-recommendations--loaded');
            return;
          } catch (error) {
            console.error(error);
          }
        }

        if (this.isConnected) this.hide();
      }

      // Nothing addable to offer: drop the placeholder.
      hide() {
        this.hidden = true;
        this.removeAttribute('aria-busy');
      }
    }
  );
}

if (!window.cartDrawerRecommendationsInitialized) {
  window.cartDrawerRecommendationsInitialized = true;

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-recommendation-add]');
    if (!button || button.disabled) return;

    // On the cart page the list sits under the product list and refreshes the
    // page's sections instead of opening the drawer.
    const cartPage = button.closest('cart-items');
    const cartDrawer = cartPage ? null : document.querySelector('cart-drawer');
    const sectionIds = cartPage
      ? [...new Set(cartPage.getSectionsToRender().map((section) => section.section))]
      : cartDrawer?.getSectionsToRender().map((section) => section.id) || [];
    const variantId = Number(button.dataset.variantId);
    const spinner = button.querySelector('.loading__spinner');

    button.disabled = true;
    button.classList.add('loading');
    spinner?.classList.remove('hidden');

    try {
      const response = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message);

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendations',
          productVariantId: variantId,
          cartData: data,
        });
      }
      // The add response does not always carry the rendered sections (the local
      // theme preview never does), so fetch them through the Section Rendering
      // API when they are missing.
      let sections = data.sections;
      if (!sections && sectionIds.length) {
        sections = await (await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)).json();
      }

      // Re-renders the drawer (items, totals, free shipping bar) and, with it,
      // this list without the product that was just added.
      if (sections) cartDrawer?.renderContents({ ...data, sections });

      // Cart page: items, totals and free shipping bar, as after a quantity
      // change in cart.js. The list itself is reloaded by cart-items through
      // the cartUpdate event published above.
      if (sections && cartPage) {
        cartPage.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container || !sections[section.section]) return;
          const target = container.querySelector(section.selector) || container;
          target.innerHTML = cartPage.getSectionInnerHTML(sections[section.section], section.selector);
        });
      }
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  });
}
